import { position, autoUpdate, ensureId } from '../floating.js';
import { setupMenu, focusItem, syncChoiceItems } from '../menu.js';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * <mellow-dropdown>: the first child is the trigger, the [popover] child is the panel
 * (a menu or free-form popover content). Uses the native Popover API for top-layer
 * rendering, light dismiss and Escape.
 */
export class MellowDropdown extends HTMLElement {
    connectedCallback() {
        if (this._ready) return this.sync();
        this._ready = true;

        this.addEventListener('keydown', this.onTriggerKeydown);
        this.addEventListener('toggle', this.onToggle, true);

        if (this.panel?.matches('[role="menu"]')) {
            setupMenu(this.panel, { close: (restore) => this.close(restore) });
        }

        this.sync();
    }

    get panel() {
        return this.querySelector(':scope > [popover]');
    }

    get trigger() {
        const first = [...this.children].find((child) => !child.hasAttribute('popover'));
        return first?.matches(FOCUSABLE) ? first : first?.querySelector(FOCUSABLE);
    }

    /** (Re)apply ARIA state – also after Livewire morphs removed it. */
    sync() {
        const { trigger, panel } = this;
        if (!trigger || !panel) return;

        trigger.setAttribute('aria-haspopup', panel.matches('[role="menu"]') ? 'menu' : 'dialog');
        trigger.setAttribute('aria-expanded', String(panel.matches(':popover-open')));
        trigger.setAttribute('aria-controls', ensureId(panel, 'mellow-panel'));

        // popovertarget gives us correct toggling (incl. light dismiss) for free on buttons.
        if (trigger instanceof HTMLButtonElement) trigger.popoverTargetElement = panel;
        else if (!trigger._mellowClick) {
            trigger._mellowClick = true;
            trigger.addEventListener('click', () => panel.togglePopover());
        }

        syncChoiceItems(panel);
    }

    open(focus = 'first') {
        const panel = this.panel;
        if (!panel) return;
        if (!panel.matches(':popover-open')) panel.showPopover();

        // Focus synchronously – the toggle event is async and keys pressed right after
        // opening would otherwise act on the trigger.
        if (panel.matches('[role="menu"]')) {
            focusItem(panel, focus);
            this._focused = true;
        }
    }

    close(restoreFocus = true) {
        const panel = this.panel;
        if (!panel?.matches(':popover-open')) return;
        panel.hidePopover();
        if (restoreFocus) this.trigger?.focus();
    }

    onTriggerKeydown = (event) => {
        if (event.target !== this.trigger || !this.panel?.matches('[role="menu"]')) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            this.open(event.key === 'ArrowUp' ? 'last' : 'first');
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.panel.matches(':popover-open') ? this.close() : this.open('first');
        }
    };

    onToggle = (event) => {
        const panel = this.panel;
        if (event.target !== panel) return;

        const open = event.newState === 'open';
        this.trigger?.setAttribute('aria-expanded', String(open));

        if (open) {
            const placement = this.getAttribute('position') ?? 'bottom';
            const align = this.getAttribute('align') ?? 'start';
            this._cleanup = autoUpdate(this.trigger ?? this, panel, () => position(this.trigger ?? this, panel, { placement, align }));

            syncChoiceItems(panel);

            if (panel.matches('[role="menu"]')) {
                // Keyboard opening already focused an item; pointer users land on the menu itself.
                if (!this._focused && !panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
            } else {
                panel.querySelector('[autofocus]')?.focus();
            }
            this._focused = false;
        } else {
            this._cleanup?.();
            if (panel.contains(document.activeElement)) this.trigger?.focus();
        }
    };
}

/**
 * <mellow-context>: opens its [popover] menu at the pointer on right-click,
 * or at the element with the ContextMenu key / Shift+F10.
 */
export class MellowContext extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        const panel = () => this.querySelector(':scope > [popover]');

        const openAt = (rect) => {
            const menu = panel();
            if (!menu) return;
            menu.showPopover();
            this._cleanup?.();
            this._cleanup = autoUpdate(null, menu, () => position({ getBoundingClientRect: () => rect }, menu, { placement: 'bottom', align: 'start', offset: 2 }));
            focusItem(menu);
        };

        // On Linux/Windows `contextmenu` fires while the button is still down. Opening right away
        // would let the following pointerup light-dismiss the new popover, so wait for it.
        let pressed = false;
        this.addEventListener('pointerdown', () => (pressed = true));
        window.addEventListener('pointerup', () => setTimeout(() => (pressed = false)));

        this.addEventListener('contextmenu', (event) => {
            if (panel()?.contains(event.target)) return;
            event.preventDefault();

            const rect = new DOMRect(event.clientX, event.clientY, 0, 0);
            if (pressed) {
                window.addEventListener('pointerup', () => openAt(rect), { once: true });
            } else {
                openAt(rect);
            }
        });

        this.addEventListener('keydown', (event) => {
            if ((event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) && !panel()?.contains(event.target)) {
                event.preventDefault();
                const rect = event.target.getBoundingClientRect();
                openAt(new DOMRect(rect.left, rect.bottom, 0, 0));
            }
        });

        this.addEventListener('toggle', (event) => {
            if (event.target === panel() && event.newState === 'closed') {
                this._cleanup?.();
                this._cleanup = null;
            }
        }, true);

        const menu = panel();
        if (menu) {
            setupMenu(menu, {
                close: () => menu.hidePopover(),
            });
        }
    }
}
